export type MapsFrontendFailure =
  | "configuration"
  | "script_timeout"
  | "script_load_failed"
  | "initialization"
  | "authentication";

const LOAD_TIMEOUT_MS = 10_000;
const API_READINESS_POLL_MS = 25;
const REQUIRED_LIBRARIES = ["maps", "marker", "places", "geocoding", "geometry"] as const;
const API_SCRIPT_URL = "https://maps.googleapis.com/maps/api/js";

type RuntimeWindow = typeof globalThis & {
  google?: { maps?: { importLibrary?: (library: string) => Promise<unknown> } };
  gm_authFailure?: (...args: unknown[]) => void;
  __odsMapsAuthListeners?: Set<() => void>;
  __odsMapsAuthObserverInstalled?: boolean;
};

type LoaderConfig = { apiKey: string; mapId: string };
type LoadGeneration = {
  id: number;
  config: LoaderConfig;
  promise: Promise<void>;
  resolve: () => void;
  reject: (error: Error) => void;
  timeout: ReturnType<typeof globalThis.setTimeout>;
  settled: boolean;
  cleanup?: () => void;
};
type ApiScriptLoad = {
  config: LoaderConfig;
  script: HTMLScriptElement;
  ownsScript: boolean;
  promise: Promise<void>;
  resolve: () => void;
  reject: (error: Error) => void;
  settled: boolean;
  cleanup: () => void;
};

let generation: LoadGeneration | null = null;
let apiScriptLoad: ApiScriptLoad | null = null;
let loaderConfig: LoaderConfig | null = null;
let nextGenerationId = 1;

function runtime(): RuntimeWindow {
  return globalThis as RuntimeWindow;
}

function importLibrary(): ((library: string) => Promise<unknown>) | undefined {
  return runtime().google?.maps?.importLibrary;
}

function findGoogleMapsScript(): HTMLScriptElement | null {
  const scripts = Array.from(document.scripts ?? []);
  return scripts.find(script => {
    try {
      const url = new URL(script.src, document.baseURI);
      return url.origin === "https://maps.googleapis.com" && url.pathname === "/maps/api/js";
    } catch {
      return false;
    }
  }) ?? null;
}

function verifyExistingScriptConfig(script: HTMLScriptElement, config: LoaderConfig): void {
  try {
    const url = new URL(script.src, document.baseURI);
    const existingKey = url.searchParams.get("key");
    if (existingKey && existingKey !== config.apiKey) {
      throw new Error("MAPS_CONFIGURATION_CONFLICT");
    }
  } catch (error) {
    if (error instanceof Error && error.message === "MAPS_CONFIGURATION_CONFLICT") throw error;
    // A nonstandard/prebootstrapped Maps script remains externally owned. Do
    // not alter its callback or configuration; importLibrary is the authority.
  }
}

function settle(state: LoadGeneration, error?: Error): void {
  if (state.settled) return;
  state.settled = true;
  globalThis.clearTimeout(state.timeout);
  state.cleanup?.();
  if (generation === state && error) generation = null;
  if (error) state.reject(error);
  else state.resolve();
}

function createApiScriptLoad(config: LoaderConfig, existingScript: HTMLScriptElement | null, generationId: number): ApiScriptLoad {
  if (apiScriptLoad) {
    if (apiScriptLoad.config.apiKey !== config.apiKey || apiScriptLoad.config.mapId !== config.mapId) {
      throw new Error("MAPS_CONFIGURATION_CONFLICT");
    }
    return apiScriptLoad;
  }

  const ownsScript = existingScript === null;
  const script = existingScript ?? document.createElement("script");
  let resolve!: () => void;
  let reject!: (error: Error) => void;
  const promise = new Promise<void>((res, rej) => { resolve = res; reject = rej; });
  const state: ApiScriptLoad = {
    config, script, ownsScript, promise, resolve, reject, settled: false,
    cleanup: () => {
      script.removeEventListener("load", onLoad);
      script.removeEventListener("error", onError);
    },
  };
  const onLoad = () => {
    if (state.settled) return;
    state.settled = true;
    state.cleanup();
    state.resolve();
  };
  const onError = () => {
    if (state.settled) return;
    state.settled = true;
    state.cleanup();
    if (state.ownsScript) state.script.remove();
    if (apiScriptLoad === state) apiScriptLoad = null;
    state.reject(new Error("MAPS_SCRIPT_LOAD_FAILED"));
  };

  if (ownsScript) {
    script.async = true;
    script.dataset.odsGoogleMapsLoader = String(generationId);
    script.crossOrigin = "anonymous";
    const url = new URL(API_SCRIPT_URL);
    url.searchParams.set("key", config.apiKey);
    url.searchParams.set("v", "weekly");
    url.searchParams.set("map_ids", config.mapId);
    url.searchParams.set("loading", "async");
    script.src = url.toString();
  } else {
    verifyExistingScriptConfig(script, config);
  }

  // Event listeners preserve any callback/configuration installed by another
  // Maps owner. The load event means download completed, not that importLibrary
  // is already exposed; API readiness is checked separately below.
  script.addEventListener("load", onLoad, { once: true });
  script.addEventListener("error", onError, { once: true });
  apiScriptLoad = state;
  if (ownsScript) {
    try {
      document.head.appendChild(script);
    } catch (error) {
      state.cleanup();
      state.settled = true;
      apiScriptLoad = null;
      state.reject(error instanceof Error ? error : new Error("MAPS_SCRIPT_LOAD_FAILED"));
    }
  }
  return state;
}

function waitForImportLibrary(state: LoadGeneration): Promise<void> {
  if (importLibrary()) return Promise.resolve();
  return new Promise<void>((resolve, reject) => {
    let timer: ReturnType<typeof globalThis.setTimeout> | null = null;
    let finished = false;
    const finish = (error?: Error) => {
      if (finished) return;
      finished = true;
      if (timer !== null) globalThis.clearTimeout(timer);
      if (state.cleanup === cancel) state.cleanup = undefined;
      if (error) reject(error);
      else resolve();
    };
    const cancel = () => finish(new Error("MAPS_SCRIPT_TIMEOUT"));
    const check = () => {
      if (state.settled || generation !== state) {
        finish(new Error("MAPS_SCRIPT_TIMEOUT"));
      } else if (importLibrary()) {
        finish();
      } else {
        timer = globalThis.setTimeout(check, API_READINESS_POLL_MS);
      }
    };
    state.cleanup = cancel;
    check();
  });
}

async function ensureApiScript(state: LoadGeneration): Promise<void> {
  const existingScript = findGoogleMapsScript();
  if (existingScript) verifyExistingScriptConfig(existingScript, state.config);
  // A pre-existing Google bootstrap may still be resolving its own script.
  // Use its documented importLibrary API without replacing global callbacks or
  // appending a competing script.
  if (importLibrary()) return;

  const scriptLoad = createApiScriptLoad(state.config, existingScript, state.id);
  await scriptLoad.promise;
  await waitForImportLibrary(state);
}

async function initializeLibraries(state: LoadGeneration): Promise<void> {
  const loadLibrary = importLibrary();
  if (!loadLibrary) throw new Error("MAPS_INITIALIZATION_FAILED");
  await Promise.all(REQUIRED_LIBRARIES.map(library => loadLibrary(library)));
  if (state.settled || generation !== state) return;
  settle(state);
}

async function initializeGeneration(state: LoadGeneration): Promise<void> {
  await ensureApiScript(state);
  if (state.settled || generation !== state) return;
  await initializeLibraries(state);
}

function startGeneration(config: LoaderConfig): LoadGeneration {
  let resolve!: () => void;
  let reject!: (error: Error) => void;
  const promise = new Promise<void>((res, rej) => { resolve = res; reject = rej; });
  const state = {
    id: nextGenerationId++, config, promise, resolve, reject,
    timeout: 0 as unknown as ReturnType<typeof globalThis.setTimeout>,
    settled: false, cleanup: undefined,
  } satisfies LoadGeneration;
  generation = state;
  state.timeout = globalThis.setTimeout(() => settle(state, new Error("MAPS_SCRIPT_TIMEOUT")), LOAD_TIMEOUT_MS);
  void initializeGeneration(state).catch(error => {
    if (state.settled || generation !== state) return;
    settle(state, error instanceof Error ? error : new Error("MAPS_INITIALIZATION_FAILED"));
  });
  return state;
}

export function subscribeMapsAuthenticationFailure(listener: () => void): () => void {
  const currentRuntime = runtime();
  const listeners = currentRuntime.__odsMapsAuthListeners ??= new Set<() => void>();
  listeners.add(listener);
  if (!currentRuntime.__odsMapsAuthObserverInstalled) {
    const previous = currentRuntime.gm_authFailure;
    currentRuntime.gm_authFailure = (...args: unknown[]) => {
      try { previous?.(...args); }
      finally {
        currentRuntime.__odsMapsAuthListeners?.forEach(notify => {
          try { notify(); } catch { /* A diagnostic listener must not break Google's callback. */ }
        });
      }
    };
    currentRuntime.__odsMapsAuthObserverInstalled = true;
  }
  return () => listeners.delete(listener);
}

export function classifyMapsFrontendFailure(error: unknown): MapsFrontendFailure {
  const code = String((error as { message?: unknown } | null)?.message ?? "");
  if (code.includes("MAPS_CONFIGURATION_MISSING") || code.includes("MAPS_CONFIGURATION_CONFLICT")) return "configuration";
  if (code.includes("MAPS_SCRIPT_TIMEOUT")) return "script_timeout";
  if (code.includes("MAPS_SCRIPT_LOAD_FAILED")) return "script_load_failed";
  if (code.includes("MAPS_AUTHENTICATION_FAILED")) return "authentication";
  return "initialization";
}

export function loadMapsFrontendScript(apiKey: string | undefined, mapId: string | undefined): Promise<void> {
  if (!apiKey?.trim() || !mapId?.trim()) return Promise.reject(new Error("MAPS_CONFIGURATION_MISSING"));
  const config = { apiKey: apiKey.trim(), mapId: mapId.trim() };
  if (loaderConfig && (loaderConfig.apiKey !== config.apiKey || loaderConfig.mapId !== config.mapId)) {
    return Promise.reject(new Error("MAPS_CONFIGURATION_CONFLICT"));
  }
  loaderConfig ??= config;
  if (generation) {
    if (generation.config.apiKey !== config.apiKey || generation.config.mapId !== config.mapId) {
      return Promise.reject(new Error("MAPS_CONFIGURATION_CONFLICT"));
    }
    return generation.promise;
  }
  return startGeneration(config).promise;
}
